/**
 * Reports Page (iter289 redesign)
 * ================================
 * Replaces the old lead-generation / sales chart with 8 configurable KPI cards
 * that draw from the same DB fields as the Account tab and Master Form module:
 *
 *   1. All Leads
 *   2. Closed Won Leads
 *   3. Closed Lost Leads
 *   4. Active Properties
 *   5. Inactive Properties
 *   6. All Master Forms
 *   7. Master Form Mapping Pending
 *   8. Terminated (Hand Over OTA)
 *
 * Date filter (top-right): Today | Single Date | Custom Date | All. Defaults
 * to Today. Cards respect the "Reports Configuration" toggles set in Settings.
 * Clicking any card opens a drilldown modal listing the underlying records.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Users, Trophy, XCircle, Building2, MinusCircle,
  ClipboardList, Link2Off, LogOut, X, Loader2, Calendar, RefreshCw,
  Smartphone, UserCheck, Footprints
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const METRIC_STYLES = {
  all_leads:                    { Icon: Users,        accent: 'bg-sky-50 text-sky-600 border-sky-200'      },
  closed_won:                   { Icon: Trophy,       accent: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  closed_lost:                  { Icon: XCircle,      accent: 'bg-rose-50 text-rose-600 border-rose-200'   },
  active_properties:            { Icon: Building2,    accent: 'bg-teal-50 text-teal-600 border-teal-200'   },
  inactive_properties:          { Icon: MinusCircle,  accent: 'bg-slate-50 text-slate-600 border-slate-200' },
  all_master_forms:             { Icon: ClipboardList,accent: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  master_form_mapping_pending:  { Icon: Link2Off,     accent: 'bg-amber-50 text-amber-600 border-amber-200' },
  terminated_hand_over_ota:     { Icon: LogOut,       accent: 'bg-red-50 text-red-600 border-red-200'      },
  total_app_login:              { Icon: Smartphone,   accent: 'bg-blue-50 text-blue-600 border-blue-200'   },
  total_active_members:         { Icon: UserCheck,    accent: 'bg-green-50 text-green-600 border-green-200' },
  walk_in_bookings:             { Icon: Footprints,   accent: 'bg-orange-50 text-orange-600 border-orange-200' },
};

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const todayYMD = () => new Date().toISOString().slice(0, 10);

const DateFilter = ({ value, onChange }) => {
  const { mode, single, start, end } = value;
  return (
    <div className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
      <Calendar size={16} className="text-gray-500" />
      <select
        data-testid="reports-date-mode"
        value={mode}
        onChange={(e) => onChange({ ...value, mode: e.target.value })}
        className="text-sm border-0 focus:ring-0 bg-transparent text-gray-800 font-medium"
      >
        <option value="today">Today</option>
        <option value="single">Single Date</option>
        <option value="custom">Custom Date</option>
        <option value="all">All</option>
      </select>
      {mode === 'single' && (
        <input
          type="date" data-testid="reports-single-date"
          value={single} onChange={(e) => onChange({ ...value, single: e.target.value })}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1"
        />
      )}
      {mode === 'custom' && (
        <>
          <input
            type="date" data-testid="reports-start-date"
            value={start} onChange={(e) => onChange({ ...value, start: e.target.value })}
            className="text-sm border border-gray-200 rounded-lg px-2 py-1"
          />
          <span className="text-gray-400 text-sm">→</span>
          <input
            type="date" data-testid="reports-end-date"
            value={end} onChange={(e) => onChange({ ...value, end: e.target.value })}
            className="text-sm border border-gray-200 rounded-lg px-2 py-1"
          />
        </>
      )}
    </div>
  );
};

const MetricCard = ({ metricKey, label, count, onClick, kind, iconName }) => {
  const style = METRIC_STYLES[metricKey] || {
    Icon: METRIC_STYLES.all_leads.Icon,
    accent: 'bg-violet-50 text-violet-600 border-violet-200',
  };
  const Icon = style.Icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`report-card-${metricKey}`}
      className="text-left bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 group"
    >
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center border ${style.accent}`}>
          <Icon size={20} />
        </div>
        <span className="text-[11px] text-gray-400 uppercase tracking-wider group-hover:text-teal-600 transition-colors">View →</span>
      </div>
      <div className="mt-4">
        <div className="text-3xl font-bold text-gray-900 tabular-nums" data-testid={`report-count-${metricKey}`}>
          {typeof count === 'number' ? count.toLocaleString() : '—'}
        </div>
        <div className="text-sm text-gray-500 mt-1">{label}</div>
      </div>
    </button>
  );
};

const DrilldownModal = ({ metric, label, filter, onClose }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ metric, date_mode: filter.mode, limit: '200' });
        if (filter.mode === 'single' && filter.single) qs.set('single_date', filter.single);
        if (filter.mode === 'custom' && filter.start && filter.end) {
          qs.set('start_date', filter.start);
          qs.set('end_date', filter.end);
        }
        const res = await fetch(`${API_BASE}/api/reports/drilldown?${qs.toString()}`, { headers: authHeader() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [metric, filter]);

  const isMasterForm = metric === 'all_master_forms' || metric === 'master_form_mapping_pending';
  const isAppLogin = metric === 'total_app_login' || metric === 'total_active_members';
  const isWalkInBookings = metric === 'walk_in_bookings';
  const isCustom = data?.kind === 'custom';
  const customSource = data?.data_source;

  const renderRow = (r) => {
    if (isWalkInBookings) {
      // iter305/306 — one row per property, aggregated in the backend.
      const last = r.last_walk_in && r.last_walk_in !== '—'
        ? (r.last_walk_in.length > 10 ? new Date(r.last_walk_in).toLocaleString() : r.last_walk_in)
        : '—';
      const amt = (typeof r.total_amount === 'number')
        ? r.total_amount.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : '—';
      return (
        <>
          <td className="py-2 pr-4 font-medium text-gray-800">{r.hotel_name || '—'}</td>
          <td className="py-2 pr-4 text-gray-600 font-mono">{r.phone || '—'}</td>
          <td className="py-2 pr-4 text-gray-500">{last}</td>
          <td className="py-2 pr-4 text-gray-600">{r.checkin || '—'}</td>
          <td className="py-2 pr-4 text-gray-600">{r.checkout || '—'}</td>
          <td className="py-2 pr-4 text-gray-800 tabular-nums">{amt}</td>
          <td className="py-2 pr-4 text-gray-700 tabular-nums">{r.total_rooms_sold ?? '—'}</td>
          <td className="py-2 pr-4">
            <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-xs border border-orange-200">
              {r.walk_in_count} walk-in{r.walk_in_count === 1 ? '' : 's'}
            </span>
          </td>
        </>
      );
    }
    if (isAppLogin) {
      const lastLogin = r.last_login ? new Date(r.last_login).toLocaleString() : '—';
      // iter298 — Login Method chip colour hints at OTP vs Email so
      // leaders can scan the mix at a glance.
      const method = (r.login_method || '—').toString();
      const methodStyle = /email/i.test(method)
        ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
        : /otp/i.test(method)
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-gray-100 text-gray-600 border-gray-200';
      return (
        <>
          <td className="py-2 pr-4 font-medium text-gray-800">{r.hotel_name || '—'}</td>
          <td className="py-2 pr-4 text-gray-600 font-mono">{r.phone || '—'}</td>
          <td className="py-2 pr-4">
            <span className={`px-2 py-0.5 rounded-full text-xs border ${methodStyle}`}>{method}</span>
          </td>
          <td className="py-2 pr-4 text-gray-700 font-mono text-xs break-all">{r.identifier || '—'}</td>
          <td className="py-2 pr-4 text-gray-500">{lastLogin}</td>
          <td className="py-2 pr-4"><span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">{r.login_count} login{r.login_count === 1 ? '' : 's'}</span></td>
        </>
      );
    }
    if (isCustom) {
      // Generic 4-column presentation using the most common identity fields per source
      if (customSource === 'external_bookings') {
        // iter304 — Walk-in Bookings drill-down maps directly to Admin
        // Panel `/api/bookings/all` fields. Amount is rendered with a
        // thousands separator when numeric, and dashes when missing so
        // OTA bookings (which have no walk-in fields populated) stay
        // readable.
        const amt = (typeof r.amount === 'number') ? r.amount.toLocaleString(undefined, { maximumFractionDigits: 2 }) : (r.amount || '—');
        return (
          <>
            <td className="py-2 pr-4 font-medium text-gray-800">{r.hotel_name || '—'}</td>
            <td className="py-2 pr-4 text-gray-700">{r.guest_name || '—'}</td>
            <td className="py-2 pr-4 text-gray-600 font-mono text-xs">{r.guest_mobile || '—'}</td>
            <td className="py-2 pr-4 text-gray-600">{r.room_type || '—'}</td>
            <td className="py-2 pr-4 text-gray-500">{r.checkin || '—'}</td>
            <td className="py-2 pr-4 text-gray-500">{r.checkout || '—'}</td>
            <td className="py-2 pr-4 text-gray-800 tabular-nums">{amt}</td>
          </>
        );
      }
      if (customSource === 'properties' || customSource === 'onboarding_forms') {
        return (
          <>
            <td className="py-2 pr-4 font-medium text-gray-800">{r.propertyName || r.hotel_name || r.id}</td>
            <td className="py-2 pr-4 text-gray-600">{r.ownerName || r.owner_name || '—'}</td>
            <td className="py-2 pr-4 text-gray-600">{r.city || '—'}</td>
            <td className="py-2 pr-4"><span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">{r.propertyStatus || '—'}</span></td>
            <td className="py-2 pr-4 text-gray-500">{(r.createdAt || '').slice(0, 10) || '—'}</td>
          </>
        );
      }
      return (
        <>
          <td className="py-2 pr-4 font-medium text-gray-800">{r.hotel_name || r.propertyName || r.id}</td>
          <td className="py-2 pr-4 text-gray-600">{r.owner_name || r.ownerName || '—'}</td>
          <td className="py-2 pr-4 text-gray-600">{r.city || '—'}</td>
          <td className="py-2 pr-4"><span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">{r.lead_stage || '—'}</span></td>
          <td className="py-2 pr-4"><span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">{r.property_status || r.propertyStatus || '—'}</span></td>
        </>
      );
    }
    if (isMasterForm) {
      return (
        <>
          <td className="py-2 pr-4 font-medium text-gray-800">{r.propertyName || r.hotel_name || '—'}</td>
          <td className="py-2 pr-4 text-gray-600">{r.grb_property_id || r.leadId || '—'}</td>
          <td className="py-2 pr-4"><span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs capitalize">{r.propertyStatus || '—'}</span></td>
          <td className="py-2 pr-4 text-gray-600">{r.ownerName || '—'}</td>
          <td className="py-2 pr-4 text-gray-500">{(r.createdAt || '').slice(0, 10) || '—'}</td>
        </>
      );
    }
    return (
      <>
        <td className="py-2 pr-4 font-medium text-gray-800">{r.hotel_name || '—'}</td>
        <td className="py-2 pr-4 text-gray-600">{r.owner_name || '—'}</td>
        <td className="py-2 pr-4 text-gray-600">{r.city || '—'}</td>
        <td className="py-2 pr-4"><span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">{r.lead_stage || '—'}</span></td>
        <td className="py-2 pr-4"><span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">{r.property_status || '—'}</span></td>
        <td className="py-2 pr-4 text-gray-500">{r.lead_date || (r.created_at || '').slice(0, 10) || '—'}</td>
      </>
    );
  };

  const headerCols = isAppLogin
    ? ['Hotel Name', 'Phone', 'Login Method', 'Identifier', 'Last Login', 'Login Count']
    : isWalkInBookings
    ? ['Hotel Name', 'Phone', 'Last Walk-in Booking', 'Check-in Date', 'Check-out Date', 'Total Amount', 'Total Rooms Sold', 'Walk-in Booking Count']
    : isCustom
      ? (customSource === 'external_bookings'
          ? ['Hotel Name', 'Guest Name', 'Guest Mobile', 'Room Type', 'Check-in Date', 'Check-out Date', 'Amount']
          : (customSource === 'properties' || customSource === 'onboarding_forms'
              ? ['Property', 'Owner', 'City', 'Status', 'Created']
              : ['Hotel', 'Owner', 'City', 'Lead Stage', 'Property Status']))
      : isMasterForm
        ? ['Property', 'GRB ID / Lead', 'Status', 'Owner', 'Created']
        : ['Hotel', 'Owner', 'City', 'Lead Stage', 'Property Status', 'Lead Date'];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
         onClick={onClose} data-testid="reports-drilldown-modal">
      <div className="bg-white rounded-2xl w-full max-w-5xl mt-10 shadow-xl border border-gray-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{label}</h3>
            {data && <p className="text-xs text-gray-500 mt-0.5">Showing {data.records.length} of {data.count} record(s){data.count === 200 ? ' (capped at 200)' : ''}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="reports-drilldown-close"><X size={18} /></button>
        </div>
        <div className="p-5 max-h-[65vh] overflow-auto">
          {err && <div className="text-red-600 text-sm">Failed to load: {err}</div>}
          {!err && !data && <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-gray-400" /></div>}
          {data && data.records.length === 0 && <div className="text-center py-10 text-gray-400 text-sm">No records match this filter.</div>}
          {data && data.records.length > 0 && (
            <table className="w-full text-sm min-w-[720px]">
              <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                <tr>
                  {headerCols.map((h) => <th key={h} className="py-2 pr-4">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.records.map((r, idx) => (
                  <tr key={r.id || idx} className="hover:bg-gray-50">
                    {renderRow(r)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const Reports = () => {
  const [filter, setFilter] = useState({
    mode: 'today',
    single: todayYMD(),
    start: todayYMD(),
    end: todayYMD(),
  });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [drill, setDrill] = useState(null);
  const abortRef = useRef();

  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setErr('');
    try {
      const qs = new URLSearchParams({ date_mode: filter.mode });
      if (filter.mode === 'single' && filter.single) qs.set('single_date', filter.single);
      if (filter.mode === 'custom' && filter.start && filter.end) {
        qs.set('start_date', filter.start);
        qs.set('end_date', filter.end);
      }
      const res = await fetch(`${API_BASE}/api/reports/summary?${qs.toString()}`, {
        headers: authHeader(), signal: ctrl.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setSummary(body);
    } catch (e) {
      if (e.name !== 'AbortError') setErr(String(e.message || e));
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const enabledCards = useMemo(() => {
    if (!summary) return [];
    const predefined = (summary.enabled_metrics || []).map((k) => ({
      key: k,
      label: summary.labels?.[k] || k,
      count: summary.metrics?.[k] ?? 0,
      kind: 'predefined',
    }));
    const custom = (summary.custom_cards || [])
      .filter((c) => c.enabled)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((c) => ({ key: c.id, label: c.name, count: c.count, kind: 'custom', icon: c.icon }));
    return [...predefined, ...custom];
  }, [summary]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Reports</h2>
          <p className="text-sm text-gray-500 mt-1">Live counts from the Accounts, Properties, and Onboarding Form modules.</p>
        </div>
        <div className="flex items-center gap-2">
          <DateFilter value={filter} onChange={setFilter} />
          <button
            onClick={load}
            data-testid="reports-refresh"
            className="p-2.5 rounded-xl bg-white border border-gray-200 shadow-sm hover:bg-gray-50 text-gray-600"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Error */}
      {err && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm" data-testid="reports-error">
          Failed to load reports: {err}
        </div>
      )}

      {/* Cards grid */}
      {loading && !summary && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-teal-600" size={28} />
        </div>
      )}
      {summary && enabledCards.length === 0 && (
        <div className="p-10 bg-white border border-gray-200 rounded-2xl text-center text-gray-500" data-testid="reports-empty">
          All report cards are disabled. Enable them in Settings → Reports Configuration.
        </div>
      )}
      {summary && enabledCards.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reports-cards-grid">
          {enabledCards.map((c) => (
            <MetricCard
              key={c.key}
              metricKey={c.key}
              label={c.label}
              count={c.count}
              kind={c.kind}
              iconName={c.icon}
              onClick={() => setDrill(c)}
            />
          ))}
        </div>
      )}

      {/* Drilldown */}
      {drill && (
        <DrilldownModal
          metric={drill.key}
          label={drill.label}
          filter={filter}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
};

export default Reports;
